"use client";

import * as Sentry from "@sentry/nextjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { BodyTitleText, BodyText } from "src/components/KinsaTypography";
import { handleManageSubscription } from "./actions";
import PRODUCT_NAME, { SUPPORT_EMAIL } from "src/lib/product-name";

/**
 * Compact inline banner shown at the top of the visit list when the user
 * has cancelled their subscription.
 *
 * The URL is prefetched so the tap can call `window.open` synchronously —
 * this app runs inside an iOS WKWebView that only hands a link off to
 * Safari when the call is made inside a user-gesture stack. Any `await`
 * before `window.open` loses that context and the open is swallowed;
 * same-tab redirects keep the user stuck inside the WebView.
 * `owlet/menu.tsx` uses the same `window.open(url, "_blank", ...)` pattern
 * for external links and reliably opens Safari.
 *
 * Stripe portal session URLs are short-lived, so we keep the prefetched
 * URL fresh with two background triggers:
 *  - short interval while the banner is mounted and visible,
 *  - visibilitychange → visible (tab switched back / app foregrounded).
 *
 * One tap must always open Safari with a fresh URL.
 */

const BACKGROUND_REFRESH_MS = 45 * 1000;

type PortalUrlState =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; message: string };

type SubscriptionBannerVariant = "kinsa" | "owlet";

export interface SubscriptionBannerProps {
  variant?: SubscriptionBannerVariant;
  /** Unix seconds when the sub ends; used by the owlet variant. */
  endsAt?: number | null;
}

export default function SubscriptionBanner({
  variant = "kinsa",
  endsAt,
}: SubscriptionBannerProps = {}) {
  const [portalState, setPortalState] = useState<PortalUrlState>({
    status: "loading",
  });
  const inFlightRef = useRef(false);

  const fetchUrl = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await handleManageSubscription();
      if ("url" in result) {
        setPortalState({ status: "ready", url: result.url });
      } else {
        setPortalState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: result.error }
        );
      }
    } catch (e) {
      Sentry.captureException(e);
      setPortalState((prev) =>
        prev.status === "ready"
          ? prev
          : {
              status: "error",
              message: "Something went wrong opening subscription management.",
            }
      );
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchUrl();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") void fetchUrl();
    }, BACKGROUND_REFRESH_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Refresh only the portal URL (short-lived). home-wizard owns the
      // subscription SWR revalidation so it fires even when this banner
      // isn't mounted (e.g. active → cancel transitions).
      fetchUrl();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchUrl]);

  const isReady = portalState.status === "ready";
  const onActivate = () => {
    if (portalState.status !== "ready") return;
    // Synchronous window.open inside the click handler preserves
    // user-gesture context; this is the path iOS WKWebView hands off
    // to Safari. Matches `handleViewAgreements` in owlet/menu.tsx.
    window.open(portalState.url, "_blank", "noopener,noreferrer");
  };

  if (variant === "owlet") {
    return (
      <OwletBannerBody
        portalState={portalState}
        endsAt={endsAt ?? null}
        disabled={!isReady}
        onActivate={onActivate}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <KinsaBannerBody
        portalState={portalState}
        disabled={!isReady}
        onActivate={onActivate}
      />

      {portalState.status === "error" && (
        <div role="alert">
          <BodyText className="text-error">
            {portalState.message} Contact us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </BodyText>
        </div>
      )}

      <div className="h-px bg-border" />
    </div>
  );
}

function KinsaBannerBody({
  portalState,
  disabled,
  onActivate,
}: {
  portalState: PortalUrlState;
  disabled: boolean;
  onActivate: () => void;
}) {
  const inner = (
    <>
      {/* Red error dot */}
      {/* TODO(sid-review): verify halo opacities (2% / 5%) against Figma — may be near-invisible on some displays. */}
      {/* TODO(sid-review): replace SVG <text> "!" with CSS overlay — <text> glyph positioning is inconsistent across browsers/OS. */}
      <div className="shrink-0 rounded-full p-[3.5px] bg-error/[0.02]">
        <div className="rounded-full p-[3.5px] bg-error/[0.05]">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="text-error"
          >
            <circle cx="9" cy="9" r="9" fill="currentColor" />
            <text
              x="9"
              y="13"
              textAnchor="middle"
              fill="white"
              fontSize="13"
              fontWeight="bold"
              fontFamily="sans-serif"
            >
              !
            </text>
          </svg>
        </div>
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <BodyTitleText>{`You've canceled your subscription.`}</BodyTitleText>
        <BodyText className="text-foreground-muted">
          {`Reactivate now to restore full ${PRODUCT_NAME} benefits.`}
        </BodyText>
      </div>

      <div className="shrink-0">
        {portalState.status === "loading" ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-foreground-muted border-t-transparent" />
        ) : (
          <svg
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="text-foreground-muted"
          >
            <path
              d="M1.5 1L6.5 6L1.5 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </>
  );

  return (
    <button
      type="button"
      disabled={disabled}
      className="flex items-center gap-2 text-left w-full"
      aria-label="Reactivate subscription"
      aria-busy={portalState.status === "loading"}
      onClick={onActivate}
    >
      {inner}
    </button>
  );
}

function OwletBannerBody({
  portalState,
  endsAt,
  disabled,
  onActivate,
}: {
  portalState: PortalUrlState;
  endsAt: number | null;
  disabled: boolean;
  onActivate: () => void;
}) {
  const formattedEnd = endsAt
    ? `ends ${new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(endsAt * 1000)}`
    : "is ending soon";

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label="Reactivate subscription"
      aria-busy={portalState.status === "loading"}
      onClick={onActivate}
      className="flex w-full items-center gap-2 rounded-2xl bg-[#bbacd3] px-6 py-4 text-left text-[#060727]"
    >
      <div className="shrink-0 self-stretch pt-[5px]">
        <div className="relative size-4 rounded-full bg-[#ee4d4d]">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="absolute inset-0 m-auto size-[8px]"
          >
            <path
              d="M8 13.5c-.3 0-.6-.1-.9-.3C4.3 11.3 1 8.8 1 5.7 1 3.9 2.4 2.5 4.2 2.5c1.1 0 2.1.5 2.8 1.4l1 1.2 1-1.2c.7-.9 1.7-1.4 2.8-1.4 1.8 0 3.2 1.4 3.2 3.2 0 3.1-3.3 5.6-6.1 7.5-.3.2-.6.3-.9.3z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="font-bold text-base leading-6">{`You've canceled your subscription.`}</span>
        <span className="font-normal text-base leading-6">
          {`Your OnCall subscription ${formattedEnd}. Reactivate now to restore full benefits.`}
        </span>
      </div>

      <div className="shrink-0">
        {portalState.status === "loading" ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#060727]/40 border-t-transparent" />
        ) : (
          <svg
            width="17"
            height="24"
            viewBox="0 0 17 24"
            fill="none"
            aria-hidden="true"
            className="text-[#060727]"
          >
            <path
              d="M4 6L12 12L4 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </button>
  );
}
